<template>
  <div>
    <v-dialog v-model="showLocationDialog"
              max-width="500">
      <v-card class="pa-4">
        <v-layout column>
          <span class="title mb-3">Select location</span>
          <v-flex>
            <v-radio-group v-model="locationGroup">
              <v-radio
                v-for="option in locations"
                :key="option.place_id"
                :label="option.formatted_address"
                :value="option"
              ></v-radio>
            </v-radio-group>
          </v-flex>
          <v-card-actions>
            <v-spacer />
            <v-btn color="accent" @click="acceptLocation">Accept</v-btn>
            <v-btn color="accent" flat @click="showLocationDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-layout>
      </v-card>
    </v-dialog>

    <GmapMap
      v-if="showMapSmall || !$vuetify.breakpoint.xsOnly"
      ref="mapRef"
      :center="mapCenter"
      :zoom="mapZoomLevel"
      :options="mapOptions"
      @click="onMapClick"
      @dblclick.native="handleDbClick"
      map-type-id="roadmap"
      :style="styles">

      <gmap-info-window :options="infoOptions"
                        :position="infoWindowPos"
                        :opened="infoWinOpen"
                        @closeclick="closeClick()">
        <div v-html="infoContent"></div>
      </gmap-info-window>

      <gmap-info-window :options="infoOptions"
                        :position="locationWindowPos"
                        :opened="locationInfoWinOpen"
                        @closeclick="closeLocationInfoWin()">
        <v-layout v-if="selectedLocation"
                  column class="ma-2">
          <h3 class="mb-0">
            {{selectedLocation.chute.label}}
            <i> ({{selectedLocation.chute.companyName}})</i>
          </h3>
        </v-layout>
        <v-layout>
          <v-flex>
            <v-btn @click="setOrigin()">Origin location</v-btn>
          </v-flex>
          <v-flex>
            <v-btn @click="setDestination()">Destination location</v-btn>
          </v-flex>
        </v-layout>
      </gmap-info-window>

      <gmap-cluster zoomOnClick
                    imagePath="/static/m"
                    v-if="showLocationChutes && !singleValue">
        <GmapMarker v-for="(location, index) in loadingLocations"
                    :key="`location_${index}`"
                    @click="locationClick(location)"
                    icon="/static/location-pin25.png"
                    v-if="_.get(originMarker, 'place_id', null) !== location.googlePlace.placeId &&
                     _.get(destinationMarker, 'place_id', null) !== location.googlePlace.placeId"
                    :position="{lat: location.googlePlace.lat, lng: location.googlePlace.lng}"
                    :clickable="true"
                    :draggable="false"/>
      </gmap-cluster>

      <GmapMarker v-if="valueMarker"
                  @dragend="onDragValue"
                  icon="/static/origin-icon.png"
                  :position="valueMarker"
                  :clickable="true"
                  :draggable="true"/>

      <GmapMarker v-if="originMarker"
                  @click="toggleInfoWindow(originMarker, originNameText, 1)"
                  @dragend="onDragOrigin"
                  icon="/static/origin-icon.png"
                  :position="originMarker"
                  :clickable="true"
                  :draggable="editPosition"/>

      <GmapMarker v-if="destinationMarker"
                  @click="toggleInfoWindow(destinationMarker, destinationNameText, 2)"
                  @dragend="onDragDestination"
                  icon="/static/destination-icon.png"
                  :position="destinationMarker"
                  :clickable="true"
                  :draggable="editPosition"/>

      <GmapMarker v-if="positionMarkers"
                  v-for="(positionMarker, idx) in positionMarkers"
                  :key="'markerId_'+idx"
                  icon="/static/been-here-marker.png"
                  :position="positionMarker"
                  @click="toggleInfoWindow(positionMarker, positionMarker.infoText, 'pos_' +idx)"
                  :clickable="true"
                  :draggable="false"/>
    </GmapMap>
    <v-layout align-center v-else>
      <v-flex xs12>
        <v-btn depressed small color="primary"
               class="ml-0"
               @click="showMapSmall = true" >
          Show Map
        </v-btn>
      </v-flex>
    </v-layout>
    <v-progress-linear v-if="loading && !singleValue"
                       class="mt-0"
                       :indeterminate="true">
    </v-progress-linear>
    <v-container class="pa-0">
      <v-layout>
        <v-flex>
          <v-layout>
            <v-flex>
              <v-text-field
                type="number"
                label="Latitude"
                v-model.number="searchLat">
              </v-text-field>
            </v-flex>
            <v-flex class="ml-3">
              <v-text-field
                type="number"
                label="Longitude"
                v-model.number="searchLng">
                <v-btn flat icon color="accent"
                       class="mt-0"
                       slot="append-outer"
                       :disabled="!searchLat || !searchLng"
                       @click="centerLocation()">
                  <v-icon>my_location</v-icon>
                </v-btn>
              </v-text-field>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex v-if="!singleValue">
          <v-layout align-center justify-end row fill-height>
            <i class="text-no-wrap" v-if="loading">Loading {{$tc('company-profile.load-chutes', 2)}}...</i>
            <template v-else>
              <v-btn v-if="showLocationChutes" right
                     @click="showLocationChutes = false"
                     flat color="accent">
                Hide {{$t('company-profile.load-chute')}} locations
              </v-btn>
              <v-btn v-else right
                     @click="showLocationChutes = true"
                     flat color="primary">
                Show {{$t('company-profile.load-chute')}} locations
              </v-btn>
            </template>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import _ from 'lodash';
import API from '@/api';
import { mapGetters } from 'vuex';
import companyNamesMixin from '@/mixins/companyNames';
import pluginMixin from '@/mixins/pluginMixin';

export default {
  name: 'LoadMap',
  mixins: [
    companyNamesMixin,
    pluginMixin,
  ],
  props: {
    value: {
      type: Object,
      default: null,
    },
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '400px',
    },
    originPlaceId: {
      type: String,
      default: null,
    },
    destinationPlaceId: {
      type: String,
      default: null,
    },
    positionMarkers: {
      type: Array,
      default: null,
    },
    load: {
      type: Object,
      default: null,
    },
    disableZoom: {
      type: Boolean,
      default: false,
    },
    hideOnSmall: {
      type: Boolean,
      default: false,
    },
    editPosition: {
      type: Boolean,
      default: false,
    },
    singleValue: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showLocationChutes: true,
      searchLat: null,
      searchLng: null,
      editingOrigin: false,
      locations: [],
      locationGroup: null,
      showLocationDialog: false,
      mapCenter: null,
      mapZoomLevel: 4,
      originMarker: null,
      valueMarker: null,
      destinationMarker: null,
      infoWinOpen: false,
      infoContent: false,
      locationInfoWinOpen: false,
      locationWindowPos: null,
      infoWindowPos: null,
      currentMidx: null,
      showMapSmall: false,
      selectedLocation: null,
      infoOptions: {
        pixelOffset: {
          width: 0,
          height: -35,
        },
      },
    };
  },
  mounted() {
    this.showMapSmall = !this.hideOnSmall;
    this.directionsService = new window.google.maps.DirectionsService();
    this.directionsDisplay = new window.google.maps.DirectionsRenderer();

    if(this.searchLat && this.searchLng) {
      this.centerLocation();
    }

    if (this.showMapSmall || !this.$vuetify.breakpoint.xsOnly) {
      this.$nextTick(() => {
        this.initMap();
      });
    }
  },
  computed: {
    _() {
      return _;
    },
    ...mapGetters('companies', {
      companies: 'catalog',
      loading: 'loadingCompanies',
    }),
    mapOptions() {
      return this.disableZoom ? {
        zoomControl: false,
        gestureHandling: 'none',
        draggableCursor: '',
      } : {};
    },
    styles() {
      return `width: ${this.width}; height: ${this.height}`;
    },
    // support V2 load on transactions
    mapHolder() {
      return this.load.tacticalInfo.loadDetails || this.load.tacticalInfo.loadGroup.details;
    },
    originNameText() {
      return this.load ?
        this.$options.filters.originName(this.mapHolder || {}) : 'Origin';
    },
    destinationNameText() {
      return this.load ?
        this.$options.filters.destinationName(this.mapHolder || {}) : 'Destination';
    },
    loadingLocations() {
      const chutes = this.companies.map(comp => _.get(comp, 'loadingInfo.loadingLocations', []));
      return _.flattenDeep(chutes);
    },
  },
  watch: {
    value() {
      if (_.get(this.value, 'geometry.location', null)) {
        this.valueMarker = {
          lat: this.value.geometry.location.lat(),
          lng: this.value.geometry.location.lng(),
        };
      } else if (this.value) {
        this.updateValueMarker(this.value.place_id);
      } else {
        this.valueMarker = null;
      }
    },
    originMarker() {
      this.updateDirections();
    },
    destinationMarker() {
      this.updateDirections();
    },
    originPlaceId(placeId) {
      this.updateOriginPlaceId(placeId);
    },
    destinationPlaceId(placeId) {
      this.updateDestinationPlaceId(placeId);
    },
    positionMarkers() {
      this.updateDirections();
    },
    showMapSmall() {
      this.$nextTick(() => {
        this.initMap(this.updateDirections);
      });
    },
    currentActivePlugins(plugins) {
      this.mapCenter = _.get(plugins, 'locality.load_map.center', null);
      if (this.mapCenter) {
        this.searchLat = this.mapCenter.lat;
        this.searchLng = this.mapCenter.lng;
        this.centerLocation();
      }
    },
  },
  methods: {
    setDestination() {
      this.$emit('destinationChange', this.formatLocation(this.selectedLocation));
      this.locationInfoWinOpen = false;
    },
    setOrigin() {
      this.$emit('originChange', this.formatLocation(this.selectedLocation));
      this.locationInfoWinOpen = false;
    },
    formatLocation(location) {
      const place = location.googlePlace;
      const googleLngLat = new window.google.maps.LatLng({ lat: place.lat, lng: place.lng });
      return {
        formatted_address: place.formattedAddress,
        place_id: place.placeId,
        geometry: {
          location: googleLngLat,
        },
      };
    },
    centerLocation() {
      this.$refs.mapRef.$mapPromise.then((map) => {
        map.panTo({ lat: this.searchLat, lng: this.searchLng });
        map.setZoom(7);
      });
    },
    locationClick(location) {
      this.selectedLocation = location;
      this.locationWindowPos = {
        lat: location.googlePlace.lat,
        lng: location.googlePlace.lng,
      };

      this.locationInfoWinOpen = true;
    },
    updateValueMarker(placeId) {
      if (placeId) {
        API.map.searchCode({ placeId }).then((result) => {
          const location = _.get(result, '[0].geometry.location', null);
          if (location) {
            this.valueMarker = { lat: location.lat(), lng: location.lng() };
          }
        });
      } else {
        this.valueMarker = null;
      }
    },
    acceptLocation() {
      if (this.singleValue) {
        this.$emit('input', this.locationGroup);
      } else if (this.editingOrigin) {
        this.$emit('originChange', this.locationGroup);
      } else {
        this.$emit('destinationChange', this.locationGroup);
      }

      this.showLocationDialog = false;
    },
    openLocationDialog(locations, editingOrigin) {
      this.locations = locations;
      this.editingOrigin = editingOrigin;
      this.locationGroup = this.locations[0];
      this.showLocationDialog = true;
    },
    onDragValue(e) {
      this.getLocations(e.latLng).then((results) => {
        if (results.length > 1) {
          this.openLocationDialog(results, true);
        } else {
          this.$emit('input', results[0]);
        }
      });
    },
    onDragOrigin(e) {
      this.getLocations(e.latLng).then((results) => {
        if (results.length > 1) {
          this.openLocationDialog(results, true);
        } else {
          this.$emit('originChange', results[0]);
        }
      });
    },
    onDragDestination(e) {
      this.getLocations(e.latLng).then((results) => {
        if (results.length > 1) {
          this.openLocationDialog(results, false);
        } else {
          this.$emit('destinationChange', results);
        }
      });
    },
    getLocations(latLng) {
      return API.map.searchCode({ location: latLng });
    },
    handleDbClick() {
      if (this.clickTimeout) {
        clearTimeout(this.clickTimeout);
      }
    },
    onMapClick(e) {
      this.clickTimeout = setTimeout(() => {
        if (this.singleValue) {
          this.onDragValue(e);
        } else if (this.editPosition) {
          if (!this.originMarker) {
            this.onDragOrigin(e);
          } else if (!this.destinationMarker) {
            this.onDragDestination(e);
          }
        }
      }, 500);
    },
    closeLocationInfoWin() {
      this.locationInfoWinOpen = false;
    },
    closeClick() {
      this.infoWinOpen = false;
      this.currentMidx = null;
    },
    toggleInfoWindow(marker, text, idx) {
      this.infoWindowPos = marker;
      this.infoContent = text;

      // check if its the same marker that was selected if yes toggle
      if (this.currentMidx === idx) {
        this.infoWinOpen = !this.infoWinOpen;
      } else {
        this.infoWinOpen = true;
        this.currentMidx = idx;
      }
    },
    initMap(callback) {
      this.$refs.mapRef.$mapPromise.then((map) => {
        this.directionsDisplay.setMap(map);
        this.directionsDisplay.setOptions({ suppressMarkers: true });
        if (callback) {
          callback.call();
        }
      });
    },
    updateOriginPlaceId(placeId) {
      if (placeId) {
        API.map.searchCode({ placeId }).then((result) => {
          const location = _.get(result, '[0].geometry.location', null);
          if (location) {
            this.originMarker = {
              place_id: placeId,
              lat: location.lat(),
              lng: location.lng(),
            };
          }
        });
      } else {
        this.originMarker = null;
      }
    },
    updateDestinationPlaceId(placeId) {
      if (placeId) {
        API.map.searchCode({ placeId }).then((result) => {
          const location = _.get(result, '[0].geometry.location', null);
          if (location) {
            this.destinationMarker = {
              place_id: placeId,
              lat: location.lat(),
              lng: location.lng(),
            };
          }
        });
      } else {
        this.destinationMarker = null;
      }
    },
    fitBounds() {
      if (this.originMarker && this.destinationMarker && this.positionMarkers) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend({ lat: this.originMarker.lat, lng: this.originMarker.lng });
        bounds.extend({ lat: this.destinationMarker.lat, lng: this.destinationMarker.lng });

        this.positionMarkers.forEach((marker) => {
          bounds.extend({ lat: marker.lat, lng: marker.lng });
        });

        if (_.get(this.$refs, 'mapRef.$mapPromise', false)) {
          this.$refs.mapRef.$mapPromise.then((map) => {
            map.fitBounds(bounds);
          });
        }
      }
    },
    updateDirections() {
      if (this.originMarker && this.destinationMarker) {
        const waypoints = [];
        const loadDriverId = _.get(this.load, 'tacticalInfo.assignedTruckingCompanyDriverUserId', null);

        if (this.positionMarkers && loadDriverId) { // Include driver's positions
          _.filter(this.positionMarkers, { userId: loadDriverId }).forEach((marker) => {
            waypoints.push({
              location: { lat: marker.lat, lng: marker.lng },
              stopover: false,
            });
          });
        }

        const routeOrigin = this.originMarker ? new google.maps.LatLng(this.originMarker.lat, this.originMarker.lng) : null;
        const routeDestination = this.destinationMarker ? new google.maps.LatLng(this.destinationMarker.lat, this.destinationMarker.lng) : null;
        const routeWaypoints = waypoints.length > 0 ? waypoints : null;

        this.directionsService.route({
          origin: routeOrigin,
          destination: routeDestination,
          waypoints: routeWaypoints,
          travelMode: google.maps.TravelMode.DRIVING,
        }, (routeResult, status) => {
          if (status === 'OK') {
            this.directionsDisplay.setDirections(routeResult);
            this.$nextTick(() => {
              setTimeout(this.fitBounds, 100);
            });
          }
        });
      } else if (!this.originMarker && !this.destinationMarker) {
        // Reset directions
        if (this.directionsDisplay) {
          this.directionsDisplay.setMap(null);
        }
        if (this.showMapSmall || !this.$vuetify.breakpoint.xsOnly) {
          this.$refs.mapRef.$mapPromise.then((map) => {
            this.directionsDisplay = new window.google.maps.DirectionsRenderer();
            this.directionsDisplay.setMap(map);
            this.directionsDisplay.setOptions({ suppressMarkers: true });
            map.setZoom(4);
            map.panTo({ lat: _.get(this.currentActivePlugins, 'locality.load_map.center.lat'), lng: _.get(this.currentActivePlugins, 'locality.load_map.center.lng') });
          });
        }
      }
    },
  },
  created() {
    const center = _.get(this.currentActivePlugins, 'locality.load_map.center', null);

    this.mapCenter = { ...center };

    this.locationWindowPos = { ...center };
    this.infoWindowPos = { ...center };

    this.updateOriginPlaceId(this.originPlaceId);
    this.updateDestinationPlaceId(this.destinationPlaceId);
  },
};
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/LoadMap.vue